import React from "react";
import { Gauge, Gift, AlertCircle } from "lucide-react";

// Customer-visible minute meter. Reads GET /api/account/usage:
//   { plan_granted, plan_available, bonus_available, total_available, reset_at }
// The denominator is ALWAYS plan_granted (never hardcoded) so the single
// 100-minute plan, a transitional legacy grant (e.g. an old 250-minute
// period), a comp/bonus-only account, or a no-subscription account each
// render honestly. Loading and error states are self-contained — this card
// never blocks the video workspace below it.

const fmtDate = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
    });
};

const Shell = ({ children }) => (
    <div
        data-testid="usage-meter"
        className="mt-6 rounded-lg border border-white/12 bg-surface-panel p-4 sm:p-5"
    >
        {children}
    </div>
);

export const UsageMeter = ({ usage, error = false }) => {
    // ERROR — compact, non-blocking. Workspace stays fully usable below.
    if (error) {
        return (
            <Shell>
                <div
                    data-testid="usage-meter-error"
                    className="flex items-center gap-2.5 text-sm text-white/50"
                >
                    <AlertCircle className="h-4 w-4 flex-none text-amber-300/80" />
                    Usage is unavailable right now — you can still upload and
                    process videos.
                </div>
            </Shell>
        );
    }

    // LOADING — light skeleton, no layout jump.
    if (!usage) {
        return (
            <Shell>
                <div
                    data-testid="usage-meter-loading"
                    className="flex items-center gap-3"
                >
                    <div className="h-4 w-4 animate-pulse rounded-full bg-white/15" />
                    <div className="h-3 w-40 animate-pulse rounded-full bg-white/10" />
                </div>
            </Shell>
        );
    }

    const planGranted = Number(usage.plan_granted) || 0;
    const planAvail = Number(usage.plan_available) || 0;
    const bonusAvail = Number(usage.bonus_available) || 0;
    const totalAvail = Number(usage.total_available) || 0;
    const resetLabel = fmtDate(usage.reset_at);

    const hasPlan = planGranted > 0;
    const hasBonus = bonusAvail > 0;
    const pct = hasPlan
        ? Math.max(0, Math.min(100, Math.round((planAvail / planGranted) * 100)))
        : 0;

    return (
        <Shell>
            <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2.5">
                    <span className="flex h-9 w-9 flex-none items-center justify-center rounded-xl border border-cyan-400/40 bg-cyan-500/10">
                        <Gauge className="h-4.5 w-4.5 text-cyan-300" />
                    </span>
                    <div className="min-w-0">
                        <p className="text-xs font-semibold uppercase tracking-wide text-white/45">
                            Monthly usage
                        </p>
                        {hasPlan ? (
                            <p
                                data-testid="usage-meter-plan"
                                className="text-sm font-semibold text-white sm:text-base"
                            >
                                {planAvail} of {planGranted} plan minutes
                                remaining
                            </p>
                        ) : hasBonus ? (
                            <p
                                data-testid="usage-meter-bonus-only"
                                className="text-sm font-semibold text-white sm:text-base"
                            >
                                {bonusAvail} bonus minute
                                {bonusAvail === 1 ? "" : "s"} available
                            </p>
                        ) : (
                            <p
                                data-testid="usage-meter-zero"
                                className="text-sm font-semibold text-white/70 sm:text-base"
                            >
                                No minutes available yet
                            </p>
                        )}
                    </div>
                </div>

                {hasPlan && resetLabel && (
                    <span
                        data-testid="usage-meter-reset"
                        className="flex-none rounded-full border border-white/10 bg-white/[0.04] px-2.5 py-1 text-[11px] font-medium text-white/50"
                    >
                        Resets {resetLabel}
                    </span>
                )}
            </div>

            {/* Plan progress bar — only when a plan allowance exists. */}
            {hasPlan && (
                <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-white/10">
                    <div
                        data-testid="usage-meter-bar"
                        className="h-full rounded-full bg-gradient-to-r from-cyan-400 to-purple-500 transition-[width] duration-500 ease-out"
                        style={{ width: `${pct}%` }}
                    />
                </div>
            )}

            {/* Bonus line — shown whenever bonus minutes exist alongside a plan. */}
            {hasPlan && hasBonus && (
                <div
                    data-testid="usage-meter-bonus"
                    className="mt-2.5 flex items-center gap-1.5 text-xs text-emerald-200/80"
                >
                    <Gift className="h-3.5 w-3.5 flex-none" />+{bonusAvail} bonus
                    minute{bonusAvail === 1 ? "" : "s"}
                </div>
            )}

            {/* Total — only meaningful when BOTH plan and bonus contribute. */}
            {hasPlan && hasBonus && (
                <div
                    data-testid="usage-meter-total"
                    className="mt-1 text-xs text-white/45"
                >
                    {totalAvail} minutes available in total
                </div>
            )}

            {/* Bonus-only accounts: minutes carry over and never reset. */}
            {!hasPlan && hasBonus && (
                <div
                    data-testid="usage-meter-bonus-note"
                    className="mt-2 text-xs text-white/45"
                >
                    Bonus minutes do not reset.
                </div>
            )}
        </Shell>
    );
};
